import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Skeleton } from "~/components/ui/skeleton";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { useProjectContext } from "~/pages/manifold-nextgen/components/projects/useProjectContext";
import { AgentStatus, type ConversationAgentSummary } from "~/services/agents";
import {
  ConversationConnect,
  conversationAguiUrl,
  conversationConnectPath,
} from "~/services/conversations";
import { ProjectConversation, projectConversationsPath } from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { Chat } from "./Chat";
import { ConversationProvider } from "./ConversationContext";
import { ReplayHttpAgent } from "./ReplayHttpAgent";
import { TopProgressBar } from "./TopProgressBar";
import { NEXTGEN_CHAT_EVENT, trackNextgenChatEvent } from "./analytics";
import { pendingInputOf } from "./awaitingInput";
import { Composer } from "./composer/Composer";
import { useConnectors } from "./connectors/useConnectors";
import { hasPendingFirstMessage } from "./pendingFirstMessage";
import { useConversationShare } from "./share/useConversationShare";
import { useSkills } from "./skills/useSkills";
import { useConversationName } from "./useConversationName";
import { useProjectDatasetSync } from "./useProjectDatasetSync";
import { useProjectSessionsSync } from "./useProjectSessionsSync";

const AGENT_NAME = "default";

// The user's selected next-turn agent for a conversation — the composer's active
// agent, advanced by an explicit switch or by accepting a handoff suggestion.
// Persisted in sessionStorage so the selection (and the accepted suggestion's
// "Accepted" state, which is derived from it) survives a refresh, then clears
// when the tab closes: this is transient next-turn intent, not a durable
// preference, so a much-later visit re-surfaces a pending suggestion fresh.
const nextTurnAgentStorageKey = (conversationId: string) => `next-turn-agent:${conversationId}`;

const readNextTurnAgent = (conversationId: string): string | null => {
  try {
    return sessionStorage.getItem(nextTurnAgentStorageKey(conversationId));
  } catch {
    return null;
  }
};

const writeNextTurnAgent = (conversationId: string, agentId: string) => {
  try {
    sessionStorage.setItem(nextTurnAgentStorageKey(conversationId), agentId);
  } catch {
    // Storage unavailable (e.g. private browsing) — the selection just won't
    // survive a refresh.
  }
};

// The suggesting-turn id whose handoff suggestion the user dismissed. Stored per
// conversation, keyed on the turn so dismissing one suggestion does not suppress
// a later turn's. Persisted in sessionStorage on the same terms as the next-turn
// agent: it survives a refresh, then clears when the tab closes, so a much-later
// visit re-surfaces a still-pending suggestion fresh.
const dismissedSuggestionStorageKey = (conversationId: string) =>
  `dismissed-suggestion:${conversationId}`;

const readDismissedSuggestion = (conversationId: string): string | null => {
  try {
    return sessionStorage.getItem(dismissedSuggestionStorageKey(conversationId));
  } catch {
    return null;
  }
};

const writeDismissedSuggestion = (conversationId: string, turnId: string) => {
  try {
    sessionStorage.setItem(dismissedSuggestionStorageKey(conversationId), turnId);
  } catch {
    // Storage unavailable (e.g. private browsing) — the dismissal just won't
    // survive a refresh.
  }
};

export const ConversationRoute = () => {
  const { conversationId, nextgenProjectId: projectId } = useParams<{
    conversationId: string;
    nextgenProjectId: string;
  }>();
  // A queued first message means brand-new: show the centered empty-state
  // skeleton while /connect loads, else the bottom-composer layout flashes
  // before snapping to empty. Chat consumes the queued message on mount.
  const isNewConversation = hasPendingFirstMessage(conversationId ?? "");

  // The /connect payload is a one-shot session bootstrap, not a cacheable
  // resource: once the agent is mounted, all state updates flow through SSE
  // and never back into the snapshot. We deliberately bypass SWR here so a
  // later visit to the same conversation always refetches and never rebuilds
  // the agent from a stale snapshot.
  const [connect, setConnect] = useState<ConversationConnect | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    if (!conversationId) return;
    const controller = new AbortController();
    setConnect(undefined);
    setError(undefined);
    apiClient<ConversationConnect>(conversationConnectPath(conversationId), {
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted) setConnect(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err);
      });
    return () => controller.abort();
  }, [conversationId]);

  if (!conversationId) return null;

  if (error) {
    return <ResourceLoadError error={error} resourceLabel="conversation" />;
  }

  if (!connect) {
    if (isNewConversation) return <NewConversationSkeleton />;
    return (
      <div
        className="relative flex flex-1 flex-col overflow-hidden"
        role="status"
        aria-label="Loading conversation"
        aria-busy="true"
      >
        <TopProgressBar />
        <div className="flex-1 overflow-y-auto px-6 py-4" />
        <div className="px-4 pb-4">
          <Composer disabled onSubmit={() => {}} />
        </div>
      </div>
    );
  }

  // `key={conversationId}` ensures Conversation (and its agent) is remounted
  // only when the user switches conversations.
  return (
    <Conversation
      key={conversationId}
      conversationId={conversationId}
      projectId={projectId}
      initial={connect}
    />
  );
};

// Loading placeholder for a brand-new session, mirroring Chat's centered empty
// state (agent avatar, name/description, dataset pill, composer) so the view
// doesn't shift when the real empty conversation renders.
const NewConversationSkeleton = () => (
  <div
    className="relative flex flex-1 flex-col overflow-hidden"
    role="status"
    aria-label="Starting session"
    aria-busy="true"
  >
    <TopProgressBar />
    <div className="flex flex-1 flex-col items-center justify-center gap-8 overflow-y-auto px-8 pb-8 pt-8">
      <div className="flex flex-col items-center gap-4">
        <Skeleton className="size-20 rounded-full" />
        <div className="flex flex-col items-center gap-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-8 w-56 rounded-full" />
      </div>
      <Skeleton className="h-[6.5rem] w-full max-w-2xl rounded-2xl" />
    </div>
  </div>
);

interface ConversationProps {
  conversationId: string;
  projectId: string | undefined;
  // Snapshot from /connect taken at mount time. The agent is the source
  // of truth from here on; subsequent /connect responses are ignored.
  initial: ConversationConnect;
}

const Conversation = ({ conversationId, projectId, initial }: ConversationProps) => {
  const conversationAgent = initial.agent;
  // The agent handling the next turn: seeded from the saved next-turn selection
  // (an earlier switch or accepted handoff suggestion, persisted in sessionStorage
  // so it survives a refresh), falling back to the conversation's bound agent, and
  // advanced by an explicit switch (Chat owns the switcher and calls back).
  // Skills and connectors are keyed on this live id, not the mount-time snapshot,
  // so a switch re-fetches the new agent's grants instead of running it with the
  // previous agent's allow-list. Reseeds per conversation via the `key` above.
  const [activeAgentId, setActiveAgentId] = useState<string | null>(
    () => readNextTurnAgent(conversationId) ?? conversationAgent?.id ?? null
  );
  // Advances the next-turn agent and persists it, so an explicit switch — or an
  // accepted handoff suggestion, which travels this same path — is not lost on a
  // refresh. Accepting is a client-only selection (no server write), so this
  // sessionStorage entry is the only record of it until the next turn is sent.
  const handleAgentSwitched = useCallback(
    (nextAgentId: string) => {
      setActiveAgentId(nextAgentId);
      writeNextTurnAgent(conversationId, nextAgentId);
    },
    [conversationId]
  );
  // The dismissed suggesting-turn id, seeded from sessionStorage so a dismissal
  // survives a refresh. Reseeds per conversation via the `key` above. Dismissing
  // records the turn id and persists it; ConversationTurns hides the suggestion
  // whose turn matches, leaving a later turn's suggestion untouched.
  const [dismissedSuggestionTurnId, setDismissedSuggestionTurnId] = useState<string | null>(() =>
    readDismissedSuggestion(conversationId)
  );
  const handleDismissSuggestion = useCallback(
    (turnId: string) => {
      setDismissedSuggestionTurnId(turnId);
      writeDismissedSuggestion(conversationId, turnId);
    },
    [conversationId]
  );
  // A conversation opened/viewed. Fires on mount and on any switch, so every
  // entry path — sidebar, sessions list, search, deep link — is counted once.
  useEffect(() => {
    trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.CONVERSATION_OPENED, {
      conversationId,
      projectId,
      agentId: conversationAgent?.id,
      agentName: conversationAgent?.display_name,
    });
  }, [conversationId, projectId, conversationAgent?.id, conversationAgent?.display_name]);
  const { attachedAgentsWithDetails, attachedDatasets, isOwner, sessions, featuredSessions } =
    useProjectContext();
  // Only agents the caller may actually pick: attached and ENABLED. A DISABLED
  // agent stays attached (and stays in `agentsById` below for labeling its past
  // turns) but is never offered — the same usability rule the composer switcher
  // applies. Also gates the latest turn's handoff suggestion.
  const usableAgentIds = useMemo(
    () =>
      new Set(
        (attachedAgentsWithDetails ?? [])
          .filter((a) => a.status === AgentStatus.ENABLED)
          .map((a) => a.id)
      ),
    [attachedAgentsWithDetails]
  );
  // The saved next-turn selection (sessionStorage) can name an agent since
  // disabled or detached from the project. Once the roster loads, fall back to the
  // bound agent for a selection no longer usable, so the agent-keyed reads below
  // and the next send resolve against a real agent instead of the send 4xx-ing —
  // the same fail-closed rule the switcher and the suggestion gate apply. Before
  // the roster loads nothing can be sent, so the seed is trusted as-is.
  const effectiveAgentId =
    attachedAgentsWithDetails !== undefined &&
    activeAgentId != null &&
    activeAgentId !== conversationAgent?.id &&
    !usableAgentIds.has(activeAgentId)
      ? (conversationAgent?.id ?? null)
      : activeAgentId;
  // Keyed on the live agent, like skills/connectors/planning below, so a switch
  // before the first message shows the newly-selected agent's starter prompts
  // rather than the bound agent's.
  const fullAgent = attachedAgentsWithDetails?.find((a) => a.id === effectiveAgentId);
  // The live agent's Planning capability, resolved from the roster so it follows
  // a switch — the same way skills and connectors are keyed on the live id above.
  // Falls back to the bound agent (before the roster loads or when the live agent
  // has left the project), then off.
  const planningEnabled =
    attachedAgentsWithDetails?.find((a) => a.id === effectiveAgentId)?.planning_enabled ??
    conversationAgent?.planning_enabled ??
    false;
  // Resolves a turn's handler id to its identity for per-turn labeling: the
  // project's usable agents, plus the conversation's bound agent to cover a
  // handler that has since left the project.
  const agentsById = useMemo(() => {
    const map = new Map<string, ConversationAgentSummary>();
    for (const rosterAgent of attachedAgentsWithDetails ?? []) {
      map.set(rosterAgent.id, rosterAgent);
    }
    if (conversationAgent) {
      map.set(conversationAgent.id, conversationAgent);
    }
    return map;
  }, [attachedAgentsWithDetails, conversationAgent]);
  const currentSession = sessions?.find((s) => s.id === conversationId);
  const displayName = currentSession?.display_name ?? "";

  // The rail has no notion of a conversation id, only the share token that
  // opens it — so knowing whether *this* conversation is featured means
  // cross-referencing its own share token against the project's rail.
  const { share } = useConversationShare(conversationId, true);
  const isFeatured = Boolean(share && featuredSessions?.some((f) => f.share_token === share.token));
  // scope_id → dataset name, so the picker can show which dataset a DATASET
  // skill was registered from.
  const datasetNames = useMemo(
    () => Object.fromEntries((attachedDatasets ?? []).map((d) => [d.id, d.title])),
    [attachedDatasets]
  );
  // The dataset(s) actually backing this conversation's context, from the
  // /connect snapshot — not the project's attached-dataset list, which is a
  // broader, separately-ordered set that drifts out of sync with the
  // conversation's real scope.
  const contextDatasetNames = initial.dataset_names;
  const agent = useMemo(
    () =>
      new ReplayHttpAgent({
        agentId: AGENT_NAME,
        threadId: conversationId,
        url: conversationAguiUrl(conversationId),
        headers: { Accept: "text/event-stream" },
        initialMessages: initial.messages,
        initialState: initial.state,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot at mount
    [conversationId]
  );

  // Patch the project conversations cache so the sidebar reflects the new
  // display name immediately — no refetch, no flicker. Backend has already
  // persisted by the time the event fires.
  const onConversationName = useCallback(
    (eventConversationId: string, displayName: string) => {
      if (!projectId) return;
      void mutate(
        projectConversationsPath(projectId),
        (current: { items: ProjectConversation[] } | undefined) => {
          if (!current) return current;
          return {
            ...current,
            items: current.items.map((c) =>
              c.id === eventConversationId ? { ...c, display_name: displayName } : c
            ),
          };
        },
        { revalidate: false }
      );
    },
    [projectId]
  );
  useConversationName(agent, onConversationName);
  // An agent may attach datasets it creates to the project server-side;
  // revalidate the sidebar's attached-dataset list when the run ends so they show.
  useProjectDatasetSync(agent, projectId);
  // A run can introduce a new serving agent; revalidate the session lists and the
  // featured rail's share reads when it ends so their avatars update live.
  useProjectSessionsSync(agent, projectId);

  const { skills } = useSkills(projectId, effectiveAgentId ?? undefined);
  const {
    connectors,
    selectedConnectorNames,
    toggleConnector,
    mcpGuidelines,
    forwardedConnectors,
    isLoading: connectorsLoading,
  } = useConnectors(effectiveAgentId ?? undefined, projectId);

  return (
    <ConversationProvider conversationId={conversationId}>
      <Chat
        agent={agent}
        conversationId={conversationId}
        conversationAgent={conversationAgent}
        starterPrompts={fullAgent?.starter_prompts ?? []}
        resumeInFlight={initial.status === "MESSAGE_IN_PROGRESS"}
        initialPending={pendingInputOf(initial)}
        skills={skills}
        datasetNames={datasetNames}
        connectors={connectors}
        enabledConnectorNames={selectedConnectorNames}
        onToggleConnector={toggleConnector}
        mcpGuidelines={mcpGuidelines}
        forwardedConnectors={forwardedConnectors}
        contextDatasetNames={contextDatasetNames}
        projectId={projectId}
        isOwner={isOwner}
        isFeatured={isFeatured}
        displayName={displayName}
        agentId={effectiveAgentId}
        onAgentSwitched={handleAgentSwitched}
        onDismissSuggestion={handleDismissSuggestion}
        dismissedSuggestionTurnId={dismissedSuggestionTurnId}
        agentsById={agentsById}
        usableAgentIds={usableAgentIds}
        planningEnabled={planningEnabled}
        featuredSessions={featuredSessions}
        // Hold the queued first turn until connector grants AND MCP-guidelines
        // acceptance settle; while `accepted` is null the forwarded allow-list is
        // empty, dropping the splash's connector selection from the opening message.
        connectorsLoading={connectorsLoading || mcpGuidelines.accepted === null}
      />
    </ConversationProvider>
  );
};
